import React, { useState, useEffect } from "react";
import { ChevronDown, ExternalLink, Mail, ChevronLeft, ChevronRight, Search, Users } from "lucide-react";
import { paperLink } from "../service/Operations";

const PAGE_SIZE = 10;

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

// Identifies a paper across author blocks. Titles in the source text can be
// truncated with a trailing ellipsis, so it's stripped before comparing.
function paperKey(paper) {
    if (paper.pmid) return `pmid:${paper.pmid}`;
    const title = (paper.title ?? "").toLowerCase().replace(/\s+/g, " ").trim().replace(/(\.\.\.|…)$/, "").trim();
    return `title:${title}`;
}

// Co-authors connected through exactly the same paper(s) would otherwise render
// as a run of identical cards, so they're collapsed into one card per paper set.
// Order follows the first member's position; authors whose paper set is not
// shared with anyone keep their own card.
function groupBySharedPapers(authors) {
    const units = [];
    const groups = new Map();
    for (const author of authors) {
        const papers = author.papers ?? [];
        if (papers.length === 0) {
            units.push({ type: "author", author });
            continue;
        }
        const key = `${author.paperCount}#` + [...new Set(papers.map(paperKey))].sort().join("|");
        let group = groups.get(key);
        if (!group) {
            group = { type: "group", key, papers, authors: [] };
            groups.set(key, group);
            units.push(group);
        }
        group.authors.push(author);
    }
    return units.map(u => (u.type === "group" && u.authors.length === 1 ? { type: "author", author: u.authors[0] } : u));
}

function unitSize(unit) {
    return unit.type === "group" ? unit.authors.length : 1;
}

function AuthorMeta({ author, style }) {
    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, ...style }}>
            {author.role && (
                <span className="similar-works-section-badge" style={{ background: "var(--surface-4)", color: "var(--ink-600)", border: "none", fontSize: 11.5 }}>
                    {author.role}
                </span>
            )}
            {author.orcid && (
                <a
                    href={`https://orcid.org/${author.orcid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="similar-works-section-badge"
                    style={{ background: "var(--success-bg)", color: "var(--success)", border: "none", fontSize: 11.5, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                    ORCID <ExternalLink size={10} />
                </a>
            )}
            {author.email && (
                <a
                    href={`mailto:${author.email}`}
                    className="similar-works-section-badge"
                    style={{ background: "var(--info-bg)", color: "var(--link-blue)", border: "none", fontSize: 11.5, textDecoration: "none", textTransform: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                    <Mail size={10} /> {author.email}
                    {author.emailConfidence && <span style={{ opacity: 0.7 }}> ({author.emailConfidence})</span>}
                </a>
            )}
        </div>
    );
}

function ConnectionSummary({ summary }) {
    return (
        <p style={{ fontSize: 12.5, color: "var(--ink-500)", margin: "0 0 14px", lineHeight: 1.5 }}>
            {summary}
        </p>
    );
}

function PaperList({ papers }) {
    return (
        <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 12 }}>
            <p className="stat-label" style={{ margin: 0 }}>
                {papers.length === 1 ? "Connecting paper" : `Connecting papers (${papers.length})`}
            </p>
            {papers.map((p, i) => {
                const link = paperLink(p);
                return (
                    <div
                        key={i}
                        style={{
                            padding: "10px 0",
                            borderBottom: i < papers.length - 1 ? "1px solid var(--border-subtle)" : "none",
                        }}
                    >
                        {link ? (
                            <a href={link} target="_blank" rel="noopener noreferrer" className="similar-works-title-link">
                                {p.title}
                                <ExternalLink size={11} className="similar-works-ext-icon" />
                            </a>
                        ) : (
                            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-900)", lineHeight: 1.4 }}>{p.title}</span>
                        )}
                        {(p.similarity || p.cluster) && (
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 6 }}>
                                {p.similarity && (
                                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-900)" }}>{p.similarity}</span>
                                )}
                                {p.cluster && (
                                    <span className="similar-works-section-badge" style={{ background: "var(--surface-3)", color: "var(--ink-500)", border: "none", fontSize: 11 }}>
                                        {p.cluster.replace(/^[A-Z]\d+:\s*/, "")}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function AuthorCard({ author }) {
    const [open, setOpen] = useState(false);

    const hasMeta = author.affiliation || author.role || author.orcid || author.email;

    return (
        <div className="submissions-card" style={{ marginBottom: 12 }}>
            <button className="analysis-group-header" style={open ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}} onClick={() => setOpen(o => !o)}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{author.name}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="analysis-group-count">
                        {author.paperCount} {author.paperCount === 1 ? "paper" : "papers"}
                    </span>
                    <ChevronDown size={15} className={`analysis-chevron ${open ? "analysis-chevron-open" : ""}`} />
                </div>
            </button>

            {open && (
                <div style={{ padding: "0 18px 16px" }}>
                    {author.affiliation && (
                        <p style={{ fontSize: 12.5, color: "var(--ink-400)", margin: "8px 0 0" }}>{author.affiliation}</p>
                    )}
                    {hasMeta && <AuthorMeta author={author} style={{ marginBottom: 14, marginTop: 8 }} />}
                    {author.connectionSummary && <ConnectionSummary summary={author.connectionSummary} />}
                    {author.papers && author.papers.length > 0 && <PaperList papers={author.papers} />}
                </div>
            )}
        </div>
    );
}

function SharedPaperCard({ group }) {
    const [open, setOpen] = useState(false);
    const { authors, papers } = group;

    const shownNames = authors.slice(0, 3).map(a => a.name).join(", ");
    const hiddenCount = authors.length - 3;
    const paperCaption = papers.length === 1 ? `Shared paper: ${papers[0].title}` : `${papers.length} shared papers`;
    // Members share the same papers, so their summaries normally match and are
    // shown once; if they ever differ, each researcher row shows its own.
    const summaries = [...new Set(authors.map(a => a.connectionSummary).filter(Boolean))];
    const sharedSummary = summaries.length === 1 ? summaries[0] : null;

    return (
        <div className="submissions-card" style={{ marginBottom: 12 }}>
            <button className="analysis-group-header" style={open ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, gap: 12 } : { gap: 12 }} onClick={() => setOpen(o => !o)}>
                <span style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
                    <Users size={14} style={{ color: "var(--ink-300)", flexShrink: 0, marginTop: 2 }} />
                    <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                            {shownNames}
                            {hiddenCount > 0 && <span style={{ fontWeight: 500, color: "var(--ink-400)" }}> +{hiddenCount} more</span>}
                        </span>
                        <span style={{ fontWeight: 400, fontSize: 12, color: "var(--ink-400)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {paperCaption}
                        </span>
                    </span>
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span className="analysis-group-count">{authors.length} researchers</span>
                    <ChevronDown size={15} className={`analysis-chevron ${open ? "analysis-chevron-open" : ""}`} />
                </div>
            </button>

            {open && (
                <div style={{ padding: "8px 18px 16px" }}>
                    {sharedSummary && <ConnectionSummary summary={sharedSummary} />}
                    <PaperList papers={papers} />
                    <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 12, marginTop: 2 }}>
                        <p className="stat-label" style={{ margin: 0 }}>Researchers ({authors.length})</p>
                        {authors.map((author, i) => (
                            <div key={i} style={{ padding: "10px 0", borderBottom: i < authors.length - 1 ? "1px solid var(--border-subtle)" : "none" }}>
                                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "2px 10px" }}>
                                    <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink-900)" }}>{author.name}</span>
                                    {author.affiliation && (
                                        <span style={{ fontSize: 12.5, color: "var(--ink-400)" }}>{author.affiliation}</span>
                                    )}
                                </div>
                                {(author.role || author.orcid || author.email) && <AuthorMeta author={author} style={{ marginTop: 8 }} />}
                                {!sharedSummary && author.connectionSummary && (
                                    <p style={{ fontSize: 12.5, color: "var(--ink-500)", margin: "8px 0 0", lineHeight: 1.5 }}>
                                        {author.connectionSummary}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function DeepThinkersList({ deepThinkers, loading }) {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const filtered = deepThinkers.filter(dt => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (dt.name ?? "").toLowerCase().includes(q)
            || (dt.affiliation ?? "").toLowerCase().includes(q);
    });

    useEffect(() => { setPage(1); }, [search]);

    // Pages hold PAGE_SIZE cards, but the range shown is counted in researchers
    // so it lines up with the total in the page heading.
    const units = groupBySharedPapers(filtered);
    const totalPages = Math.max(1, Math.ceil(units.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * PAGE_SIZE;
    const paginated = units.slice(pageStart, pageStart + PAGE_SIZE);
    const firstResearcher = units.slice(0, pageStart).reduce((n, u) => n + unitSize(u), 0) + 1;
    const lastResearcher = firstResearcher + paginated.reduce((n, u) => n + unitSize(u), 0) - 1;

    return (
        <>
            <div className="upload-page-title-row">
                <div>
                    <h1 className="upload-page-heading">DeepThinkers</h1>
                    {!loading && <p className="upload-page-meta">{deepThinkers.length} researcher{deepThinkers.length !== 1 ? "s" : ""} connected through the knowledge graph</p>}
                </div>
            </div>

            <div className="submissions-filters" style={{ marginBottom: 16 }}>
                <div className="submissions-search-wrap" style={{ position: "relative", marginLeft: "auto" }}>
                    <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 160 }}
                    />
                    {search && (
                        <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>×</button>
                    )}
                </div>
            </div>

            {!loading && totalPages > 1 && (
                <div className="pagination-bar" style={{ marginBottom: 12 }}>
                    <p className="pagination-count">{firstResearcher}–{lastResearcher} of {filtered.length}</p>
                    <div className="pagination-controls">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                        <div className="pagination-pages">
                            {pageWindows(safePage, totalPages).map((n, i) =>
                                n === null
                                    ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>...</span>
                                    : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                            )}
                        </div>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                    </div>
                    <div className="pagination-spacer" />
                </div>
            )}

            {loading ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="submissions-card" style={{ padding: "18px" }}>
                            <div className="skeleton" style={{ height: 14, width: "40%", marginBottom: 8 }} />
                            <div className="skeleton" style={{ height: 11, width: "25%" }} />
                        </div>
                    ))}
                </div>
            ) : filtered.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    {paginated.map(unit => (
                        unit.type === "group"
                            ? <SharedPaperCard key={`group-${unit.key}`} group={unit} />
                            : <AuthorCard key={`author-${deepThinkers.indexOf(unit.author)}`} author={unit.author} />
                    ))}
                </div>
            ) : (
                <div className="submissions-card" style={{ padding: "32px 24px", textAlign: "center" }}>
                    <p className="settings-empty">{search ? "No researchers match your search." : "No DeepThinkers data available for this submission."}</p>
                </div>
            )}
        </>
    );
}
